"use client";

import { ArrowRight, Plus, ArrowUp, ArrowDown } from "lucide-react";
import { useUserTransactionHistory } from "../hooks/query/queries";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { useState, useMemo, useCallback } from "react";
import { Skeleton } from "./ui/skeleton";
import TransactionHistoryAll from "./TransactionHistoryAll";
import TransactionHistorySingle from "./TransactionHistorySingle";
import { FormattedDate } from "../utils/dateFormatter";
import CurrencyFormatter from "../utils/currencyFormatter";
import { parseAmount } from "../utils/convertNumbers";
import { withoutStablecoinDust } from "../utils/transactionAssetHelpers";
import filterIcon from "../assets/images/trading/filter.svg";
import sortIcon from "../assets/images/trading/sort.svg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "./ui/dropdown-menu";
import {
  TRANSACTION_HISTORY_FILTER_STATUS_OPTIONS,
  TRANSACTION_HISTORY_FILTER_TYPE_OPTIONS,
} from "../constants/transactionHistoryFilters";

export function TransactionHistoryAsset({ userId, asset }) {
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("date-desc");
  const [selectedTransaction, setSelectedTransaction] = useState([]);
  const [singleTransaction, setSingleTransaction] = useState(false);
  const [transactionsAll, setTransactionsAll] = useState(false);
  const [isFromAllTr, setIsFromAllTr] = useState(false);

  const { data: transactionsComplete, isLoading } =
    useUserTransactionHistory(userId);
  const formatter = new CurrencyFormatter();

  const assetIdentifiers = useMemo(() => {
    if (!asset) return [];
    const identifiers = new Set();

    if (asset.symbol) {
      identifiers.add(asset.symbol.toUpperCase());
    }

    if (asset.asset) {
      identifiers.add(asset.asset.toUpperCase());
    }

    if (asset.info?.asset) {
      identifiers.add(asset.info.asset.toUpperCase());
    }

    return Array.from(identifiers);
  }, [asset]);

  const getAssetImage = () => {
    return asset?.image || asset?.info?.image || "";
  };

  const getNumericAmountForSorting = useCallback((tx) => {
    const subtype = tx.transactionSubtype?.toLowerCase();
    const transactionType = tx.transactionType;

    if (transactionType === "UserTrade") {
      if (subtype === "buy") {
        return parseAmount(
          tx.transactionRef?.incomingAmount?.$numberDecimal ||
            tx.transactionRef?.incomingAmount ||
            tx.transactionRef?.outgoingAmount?.$numberDecimal ||
            tx.transactionRef?.outgoingAmount ||
            tx.transactionRef?.total?.$numberDecimal ||
            tx.transactionRef?.totalAmount?.$numberDecimal ||
            tx.amount ||
            0
        );
      } else if (subtype === "sell") {
        return parseAmount(
          tx.transactionRef?.outgoingAmount?.$numberDecimal ||
            tx.transactionRef?.outgoingAmount ||
            tx.transactionRef?.incomingAmount?.$numberDecimal ||
            tx.transactionRef?.incomingAmount ||
            tx.transactionRef?.total?.$numberDecimal ||
            tx.transactionRef?.totalAmount?.$numberDecimal ||
            tx.amount ||
            0
        );
      }
    }

    return parseAmount(
      tx.transactionRef?.amount?.$numberDecimal ||
        tx.transactionRef?.amount ||
        tx.transactionRef?.totalAmount?.$numberDecimal ||
        tx.transactionRef?.total?.$numberDecimal ||
        tx.amount ||
        0
    );
  }, []);

  const filteredAndSortedTransactions = useMemo(() => {
    let transactions = transactionsComplete || [];

    if (asset && assetIdentifiers.length > 0) {
      transactions = transactions.filter((tx) => {
        const txAsset = (tx.asset || tx.currency || "").toUpperCase();
        const txIncomingAsset = tx.transactionRef?.incomingAsset?.toUpperCase();
        const txOutgoingAsset = tx.transactionRef?.outgoingAsset?.toUpperCase();

        return (
          assetIdentifiers.includes(txAsset) ||
          assetIdentifiers.includes(txIncomingAsset) ||
          assetIdentifiers.includes(txOutgoingAsset)
        );
      });
    }

    if (filterType !== "all") {
      const filterTypeLower = filterType.toLowerCase();
      transactions = transactions.filter((tx) => {
        const txSubtype = tx.transactionSubtype?.toLowerCase();
        return txSubtype === filterTypeLower;
      });
    }

    if (filterStatus !== "all") {
      const filterStatusLower = filterStatus.toLowerCase();
      transactions = transactions.filter((tx) => {
        const txStatus = tx.status?.toLowerCase();
        if (filterStatusLower === "complete") {
          return txStatus === "complete" || txStatus === "completed";
        }
        return txStatus === filterStatusLower;
      });
    }

    transactions = [...transactions].sort((a, b) => {
      if (sortBy === "date-desc") {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      } else if (sortBy === "date-asc") {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateA - dateB;
      } else if (sortBy === "amount-desc") {
        const amountA = getNumericAmountForSorting(a);
        const amountB = getNumericAmountForSorting(b);
        return amountB - amountA;
      } else if (sortBy === "amount-asc") {
        const amountA = getNumericAmountForSorting(a);
        const amountB = getNumericAmountForSorting(b);
        return amountA - amountB;
      }
      return 0;
    });

    return withoutStablecoinDust(transactions);
  }, [
    transactionsComplete,
    asset,
    assetIdentifiers,
    filterType,
    filterStatus,
    sortBy,
  ]);

  const displayTransactions = useMemo(() => {
    if (isLoading) return [];
    return filteredAndSortedTransactions || [];
  }, [filteredAndSortedTransactions, isLoading]);

  const dateFormat = useMemo(() => new FormattedDate(), []);

  const formatTransactionDate = (createdAt) => {
    if (!createdAt) return "";
    const now = new Date();
    const transactionDate = new Date(createdAt);
    const isToday = now.toDateString() === transactionDate.toDateString();

    if (isToday) {
      const hours = transactionDate.getHours();
      const minutes = transactionDate.getMinutes();
      const ampm = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, "0");
      return `Today, ${displayHours}:${displayMinutes} ${ampm}`;
    }

    return dateFormat.simpleFormat(createdAt);
  };

  const getTransactionIconComponent = (transaction) => {
    const subtype = transaction.transactionSubtype?.toLowerCase();
    if (subtype === "buy" || subtype === "deposit" || subtype === "incoming") {
      return Plus;
    } else if (
      subtype === "withdrawal" ||
      subtype === "outgoing" ||
      subtype === "send"
    ) {
      return ArrowUp;
    } else if (subtype === "receive") {
      return ArrowDown;
    } else if (subtype === "sell") {
      return Plus;
    }
    return Plus;
  };

  const getTransactionActionText = (transaction) => {
    const subtype = transaction.transactionSubtype?.toLowerCase();
    const transactionType = transaction.transactionType;

    let asset =
      transaction.asset?.toUpperCase() ||
      transaction.currency?.toUpperCase() ||
      "";

    if (transactionType === "UserTrade" && subtype === "sell") {
      asset = transaction.transactionRef?.outgoingAsset?.toUpperCase() || asset;
    } else if (transactionType === "UserTrade" && subtype === "buy") {
      asset = transaction.transactionRef?.incomingAsset?.toUpperCase() || asset;
    }

    if (subtype === "buy") {
      return `Bought ${asset}`;
    } else if (subtype === "sell") {
      return `Sold ${asset}`;
    } else if (subtype === "deposit") {
      return `Deposited ${asset}`;
    } else if (subtype === "withdrawal") {
      return `Withdrew ${asset}`;
    } else if (subtype === "send" || subtype === "outgoing") {
      return `Sent ${asset}`;
    } else if (subtype === "receive" || subtype === "incoming") {
      return `Received ${asset}`;
    }
    return `${subtype?.charAt(0).toUpperCase() + subtype?.slice(1)} ${asset}`;
  };

  const getTransactionActionTextMobile = (transaction) => {
    const subtype = transaction.transactionSubtype?.toLowerCase();

    if (subtype === "buy") {
      return "Buy";
    } else if (subtype === "sell") {
      return "Sell";
    } else if (subtype === "deposit") {
      return "Deposited";
    } else if (subtype === "withdrawal") {
      return "Withdrew";
    } else if (subtype === "send" || subtype === "outgoing") {
      return "Sent";
    } else if (subtype === "receive" || subtype === "incoming") {
      return "Received";
    }
    return subtype?.charAt(0).toUpperCase() + subtype?.slice(1) || "";
  };

  const isFiatCurrency = (asset) => {
    if (!asset) return false;
    const assetUpper = asset.toUpperCase();
    const fiatCurrencies = [
      "ZAR",
      "USD",
      "EUR",
      "GBP",
      "JPY",
      "AUD",
      "CAD",
      "CHF",
      "CNY",
    ];
    return fiatCurrencies.includes(assetUpper);
  };

  const determineSellAssets = (transaction) => {
    const asset = transaction.asset?.toUpperCase();
    const currency = transaction.currency?.toUpperCase();

    if (asset && currency) {
      const assetIsFiat = isFiatCurrency(asset);
      const currencyIsFiat = isFiatCurrency(currency);

      if (assetIsFiat && !currencyIsFiat) {
        return { outgoingAsset: currency, incomingAsset: asset };
      } else if (!assetIsFiat && currencyIsFiat) {
        return { outgoingAsset: asset, incomingAsset: currency };
      } else if (!assetIsFiat && !currencyIsFiat) {
        return { outgoingAsset: asset, incomingAsset: currency || "ZAR" };
      } else {
        return {
          outgoingAsset: asset || currency,
          incomingAsset: currency || asset || "ZAR",
        };
      }
    }

    return {
      outgoingAsset: asset || currency || "",
      incomingAsset: currency || asset || "ZAR",
    };
  };

  const getTransactionAmounts = (transaction) => {
    const subtype = transaction.transactionSubtype?.toLowerCase();
    const transactionType = transaction.transactionType;
    let primaryAmount = "";
    let secondaryAmount = "";
    let totalCost = "";

    if (transactionType === "UserTrade") {
      let incomingAsset =
        transaction.transactionRef?.incomingAsset?.toUpperCase();
      let outgoingAsset =
        transaction.transactionRef?.outgoingAsset?.toUpperCase();

      if (!incomingAsset || !outgoingAsset) {
        if (subtype === "buy") {
          incomingAsset =
            incomingAsset ||
            transaction.asset?.toUpperCase() ||
            transaction.currency?.toUpperCase() ||
            null;
          outgoingAsset =
            outgoingAsset ||
            transaction.currency?.toUpperCase() ||
            transaction.asset?.toUpperCase() ||
            "ZAR";
        } else if (subtype === "sell") {
          const sellAssets = determineSellAssets(transaction);
          incomingAsset = incomingAsset || sellAssets.incomingAsset;
          outgoingAsset = outgoingAsset || sellAssets.outgoingAsset;
        }
      }

      if (subtype === "buy") {
        const cryptoAmount = parseAmount(
          transaction.transactionRef?.incomingAmount?.$numberDecimal ||
            transaction.transactionRef?.incomingAmount ||
            transaction.transactionRef?.total?.$numberDecimal ||
            transaction.transactionRef?.totalAmount?.$numberDecimal
        );

        if (incomingAsset && (cryptoAmount > 0 || cryptoAmount === 0)) {
          primaryAmount = formatter.format(cryptoAmount, incomingAsset);
        }

        const price = parseAmount(
          transaction.transactionRef?.price?.$numberDecimal ||
            transaction.transactionRef?.price ||
            0
        );

        let fiatAmount = 0;
        if (price > 0 && cryptoAmount > 0) {
          fiatAmount = cryptoAmount * price;
        } else {
          fiatAmount = parseAmount(
            transaction.transactionRef?.outgoingAmount?.$numberDecimal ||
              transaction.transactionRef?.outgoingAmount ||
              transaction.transactionRef?.total?.$numberDecimal ||
              transaction.transactionRef?.totalAmount?.$numberDecimal ||
              transaction.transactionRef?.amount?.$numberDecimal ||
              transaction.transactionRef?.amount ||
              0
          );
        }

        if (outgoingAsset && (fiatAmount > 0 || fiatAmount === 0)) {
          secondaryAmount = formatter.format(fiatAmount, outgoingAsset);
        }

        const totalCostAmount = fiatAmount;

        if (outgoingAsset && (totalCostAmount > 0 || totalCostAmount === 0)) {
          totalCost = formatter.format(totalCostAmount, outgoingAsset);
        }
      } else if (subtype === "sell") {
        const cryptoAmount = parseAmount(
          transaction.transactionRef?.outgoingAmount?.$numberDecimal ||
            transaction.transactionRef?.outgoingAmount ||
            transaction.transactionRef?.total?.$numberDecimal ||
            transaction.transactionRef?.totalAmount?.$numberDecimal
        );

        if (outgoingAsset && (cryptoAmount > 0 || cryptoAmount === 0)) {
          primaryAmount = formatter.format(cryptoAmount, outgoingAsset);
        }

        const totalCostAmount = parseAmount(
          transaction.transactionRef?.totalAmount?.$numberDecimal ||
            transaction.transactionRef?.total?.$numberDecimal ||
            transaction.transactionRef?.outgoingAmount?.$numberDecimal ||
            transaction.transactionRef?.outgoingAmount ||
            cryptoAmount ||
            0
        );

        if (outgoingAsset && (totalCostAmount > 0 || totalCostAmount === 0)) {
          totalCost = formatter.format(totalCostAmount, outgoingAsset);
        }

        const fiatAmount = parseAmount(
          transaction.transactionRef?.incomingAmount?.$numberDecimal ||
            transaction.transactionRef?.incomingAmount ||
            transaction.transactionRef?.total?.$numberDecimal ||
            transaction.transactionRef?.totalAmount?.$numberDecimal ||
            transaction.transactionRef?.amount?.$numberDecimal
        );

        if (
          incomingAsset &&
          incomingAsset !== outgoingAsset &&
          (fiatAmount > 0 || fiatAmount === 0)
        ) {
          secondaryAmount = formatter.format(fiatAmount, incomingAsset);
        }
      }
    } else {
      const asset =
        transaction.asset?.toUpperCase() ||
        transaction.currency?.toUpperCase() ||
        "";

      let amount = 0;

      if (transactionType === "Payment") {
        amount = parseAmount(
          transaction.transactionRef?.amount?.$numberDecimal ||
            transaction.transactionRef?.amount ||
            transaction.transactionRef?.totalAmount?.$numberDecimal ||
            transaction.amount ||
            0
        );
      } else if (transactionType === "CryptoTransaction") {
        amount = parseAmount(
          transaction.transactionRef?.amount?.$numberDecimal ||
            transaction.transactionRef?.amount ||
            transaction.transactionRef?.totalAmount?.$numberDecimal ||
            transaction.transactionRef?.total?.$numberDecimal ||
            transaction.amount ||
            0
        );
      } else if (transactionType === "FiatTransaction") {
        amount = parseAmount(
          transaction.transactionRef?.amount?.$numberDecimal ||
            transaction.transactionRef?.amount ||
            transaction.transactionRef?.totalAmount?.$numberDecimal ||
            transaction.transactionRef?.total?.$numberDecimal ||
            transaction.amount ||
            0
        );
      } else {
        amount = parseAmount(
          transaction.transactionRef?.amount?.$numberDecimal ||
            transaction.transactionRef?.amount ||
            transaction.transactionRef?.totalAmount?.$numberDecimal ||
            transaction.transactionRef?.total?.$numberDecimal ||
            transaction.amount ||
            0
        );
      }

      if (asset && (amount > 0 || amount === 0)) {
        primaryAmount = formatter.format(amount, asset);
      } else if (amount > 0 || amount === 0) {
        primaryAmount = formatter.format(amount, "ZAR");
      }

      const isSendOrReceiveOrDepositOrWithdrawal =
        subtype === "send" ||
        subtype === "receive" ||
        subtype === "deposit" ||
        subtype === "withdrawal" ||
        subtype === "incoming" ||
        subtype === "outgoing";

      if (
        asset &&
        asset !== "ZAR" &&
        !isSendOrReceiveOrDepositOrWithdrawal &&
        (amount > 0 || amount === 0)
      ) {
        const zarValue = parseAmount(
          transaction.valueInZar ||
            (transaction.transactionRef?.price?.$numberDecimal &&
              amount *
                parseFloat(transaction.transactionRef.price.$numberDecimal)) ||
            transaction.transactionRef?.amount?.$numberDecimal ||
            0
        );

        if (zarValue > 0 || zarValue === 0) {
          secondaryAmount = formatter.format(zarValue, "ZAR");
        }
      }

      if (
        transactionType === "Payment" ||
        transactionType === "CryptoTransaction" ||
        transactionType === "FiatTransaction"
      ) {
        const totalAmount = parseAmount(
          transaction.transactionRef?.totalAmount?.$numberDecimal ||
            transaction.transactionRef?.total?.$numberDecimal ||
            amount
        );
        if (asset && (totalAmount > 0 || totalAmount === 0)) {
          totalCost = formatter.format(totalAmount, asset);
        }
      } else {
        totalCost = primaryAmount || "";
      }
    }

    return { primaryAmount, secondaryAmount, totalCost };
  };

  const StatusIcon = ({ color }) => (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-[10px] h-[10px]"
    >
      <g clipPath="url(#clip0_2352_21030)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5.73034 0.302516C5.32699 -0.100838 4.67302 -0.100839 4.26966 0.302515L3.41081 1.16138H2.19412C1.62369 1.16138 1.16127 1.6238 1.16127 2.19423V3.41091L0.302516 4.26966C-0.100838 4.67301 -0.100839 5.32698 0.302515 5.73034L1.16127 6.58909V7.80464C1.16127 8.37507 1.62369 8.8375 2.19412 8.8375H3.40965L4.26966 9.6975C4.67301 10.1009 5.32698 10.1009 5.73034 9.6975L6.59035 8.8375H7.8045C8.37493 8.8375 8.83736 8.37507 8.83736 7.80464V6.59045L9.6975 5.73034C10.1009 5.32699 10.1009 4.67302 9.6975 4.26966L8.83736 3.40955V2.19423C8.83736 1.6238 8.37493 1.16138 7.8045 1.16138H6.5892L5.73034 0.302516Z"
          fill={color}
        />
      </g>
      <defs>
        <clipPath id="clip0_2352_21030">
          <rect width="10" height="10" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );

  const handleOpenTHModal = () => {
    setTransactionsAll(true);
  };

  const handleReOpenTHModal = () => {
    if (isFromAllTr) {
      setTransactionsAll(true);
    }
  };

  const handleSingleTransaction = (order) => {
    setSelectedTransaction(order);
    setIsFromAllTr(false);
    setSingleTransaction(true);
    setTransactionsAll(false);
  };

  return (
    <Card className="flex flex-col md:pt-2 pt-0 mb:pb-6 pb-0 w-full lg:w-[65%] ">
      <CardHeader className="md:pb-6 pb-2">
        <CardTitle>
          <div className="flex justify-between items-center">
            <div className="text-[#6D6584] text-[20px] font-semibold">
              Transaction History
            </div>
            <div className="flex items-center justify-end gap-2">
              <div className="items-center gap-2 mr-6 hidden md:flex">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className={`h-[48px] flex items-center justify-center w-full lg:!w-[107px] bg-white border border-[#EBE9EF] text-[#6D6584] rounded-full text-[14px] font-semibold hover:bg-gray-50 transition-colors gap-2 ${
                        filterType !== "all" || filterStatus !== "all"
                          ? "border-[#EB3366] bg-[#EB3366]/5"
                          : ""
                      }`}
                    >
                      <span>Filter</span>
                      <img
                        src={filterIcon}
                        alt="filter"
                        className="w-[16px] h-[16px] text-[#6D6584]"
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
                    <DropdownMenuRadioGroup
                      value={filterType}
                      onValueChange={setFilterType}
                    >
                      {TRANSACTION_HISTORY_FILTER_TYPE_OPTIONS.map(
                        ({ value, label }) => (
                          <DropdownMenuRadioItem key={value} value={value}>
                            {label}
                          </DropdownMenuRadioItem>
                        )
                      )}
                    </DropdownMenuRadioGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                    <DropdownMenuRadioGroup
                      value={filterStatus}
                      onValueChange={setFilterStatus}
                    >
                      {TRANSACTION_HISTORY_FILTER_STATUS_OPTIONS.map(
                        ({ value, label }) => (
                          <DropdownMenuRadioItem key={value} value={value}>
                            {label}
                          </DropdownMenuRadioItem>
                        )
                      )}
                    </DropdownMenuRadioGroup>
                    {(filterType !== "all" || filterStatus !== "all") && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => {
                            setFilterType("all");
                            setFilterStatus("all");
                          }}
                          className="text-red-600 focus:text-red-600"
                        >
                          Clear Filters
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className={`h-[48px] flex items-center justify-center w-full lg:!w-[107px] bg-white border border-[#EBE9EF] text-[#6D6584] rounded-full text-[14px] font-semibold hover:bg-gray-50 transition-colors gap-2 ${
                        sortBy !== "date-desc"
                          ? "border-[#EB3366] bg-[#EB3366]/5"
                          : ""
                      }`}
                    >
                      <span>Sort</span>
                      <img
                        src={sortIcon}
                        alt="sort"
                        className="w-[16px] h-[16px] text-[#6D6584]"
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>Sort by</DropdownMenuLabel>
                    <DropdownMenuRadioGroup
                      value={sortBy}
                      onValueChange={setSortBy}
                    >
                      <DropdownMenuRadioItem value="date-desc">
                        Date (Newest First)
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="date-asc">
                        Date (Oldest First)
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="amount-desc">
                        Amount (Highest First)
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="amount-asc">
                        Amount (Lowest First)
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <Button
                onClick={handleOpenTHModal}
                variant="ghost"
                className="md:text-[#423E51] text-[#6D6584] flex items-center md:space-x-2 text-[14px] font-semibold p-0 md:border-none border border-[#EBE9EF] rounded-full md:w-auto w-[40px] justify-center p-0"
              >
                <span className="hidden md:inline">
                  View All Transaction History
                </span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="p-0 md:px-5 flex flex-col flex-1">
        {/* Column Headers */}
        <div className="px-6 pb-4 hidden md:grid grid-cols-12 gap-4">
          <div className="col-span-4 text-[#6D6584] font-semibold text-sm">
            Action
          </div>
          <div className="col-span-4 text-[#6D6584] font-semibold text-[14px]">
            Amount
          </div>
          <div className="col-span-3 text-[#6D6584] font-semibold text-[14px]">
            Status
          </div>
          <div className="col-span-1"></div>
        </div>

        <div className="md:space-y-7 space-y-3 p-6 pt-4 flex-1">
          {isLoading ? (
            Array(5)
              .fill(0)
              .map((_, index) => (
                <div
                  key={index}
                  className="flex items-center gap-8 py-2 animate-pulse"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <Skeleton className="h-12 w-12 rounded-full bg-gray-300" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-24 bg-gray-300" />
                      <Skeleton className="h-3 w-20 bg-gray-300" />
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center justify-left">
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-16 bg-gray-300" />
                      <Skeleton className="h-3 w-14 bg-gray-300" />
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-5 flex-1">
                    <div className="flex items-center justify-center">
                      <Skeleton className="h-6 w-28 rounded-full bg-gray-300" />
                    </div>
                    <div className="flex items-center justify-end">
                      <Skeleton className="h-10 w-10 rounded-full bg-gray-300" />
                    </div>
                  </div>
                </div>
              ))
          ) : displayTransactions?.slice(0, 5)?.length > 0 ? (
            displayTransactions.slice(0, 5).map((transaction) => {
              const transactionType = transaction.transactionType;
              const subtype = transaction.transactionSubtype?.toLowerCase();

              let assetSymbol =
                transaction.asset?.toUpperCase() ||
                transaction.currency?.toUpperCase() ||
                asset?.symbol?.toUpperCase() ||
                "";

              if (transactionType === "UserTrade" && subtype === "sell") {
                assetSymbol =
                  transaction.transactionRef?.outgoingAsset?.toUpperCase() ||
                  assetSymbol;
              } else if (transactionType === "UserTrade" && subtype === "buy") {
                assetSymbol =
                  transaction.transactionRef?.incomingAsset?.toUpperCase() ||
                  assetSymbol;
              }

              const assetImage = getAssetImage();
              const IconComponent = getTransactionIconComponent(transaction);
              const actionText = getTransactionActionText(transaction);
              const actionTextMobile =
                getTransactionActionTextMobile(transaction);
              const { primaryAmount, secondaryAmount } =
                getTransactionAmounts(transaction);
              const status = transaction.status;

              return (
                <div
                  key={transaction?._id}
                  className="flex items-center justify-between gap-4 md:grid md:grid-cols-12 md:gap-4 md:border-none border border-[#F1EDF7] border-[1px] rounded-2xl p-4 md:p-0"
                >
                  <div className="col-span-4 flex items-center gap-3 min-w-0">
                    <div className="relative flex-shrink-0">
                      <div className="w-[40px] h-[40px] rounded-full bg-[#EB3366] flex items-center justify-center">
                        <IconComponent className="w-[16px] h-[16px] text-white" />
                      </div>
                      {assetImage && (
                        <div className="absolute -bottom-1 -right-1 w-[23px] h-[23px] rounded-full bg-blue-500 flex items-center justify-center border-2 border-white overflow-hidden">
                          <img
                            src={assetImage}
                            alt={assetSymbol}
                            className="w-[23px] h-[23px] object-contain"
                          />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1 md:space-y-2 space-y-1">
                      <div className="font-semibold text-[#33125F] text-[16px] md:text-[20px]">
                        <span className="md:hidden">{actionTextMobile}</span>
                        <span className="hidden md:inline">{actionText}</span>
                      </div>
                      <div className="text-[#6D6584] text-xs md:text-[14px]">
                        {formatTransactionDate(transaction.createdAt)}
                      </div>
                    </div>
                  </div>

                  <div className="col-span-4 hidden md:flex flex-col space-y-2">
                    <div className="font-semibold text-[#33125F] text-[16px] md:text-[20px]">
                      {primaryAmount}
                    </div>
                    {secondaryAmount && (
                      <div className="text-[#6D6584] text-xs md:text-[14px]">
                        {secondaryAmount}
                      </div>
                    )}
                  </div>

                  <div className="md:hidden flex flex-col items-end">
                    <div className="font-semibold text-[#33125F] text-[16px] md:text-[20px]">
                      {primaryAmount}
                    </div>
                    {secondaryAmount && (
                      <div className="text-[#6D6584] text-xs md:text-[14px]">
                        {secondaryAmount}
                      </div>
                    )}
                    {status && (
                      <div className="flex items-center gap-2 text-[14px] font-bold mt-1">
                        {status === "Pending" ||
                        status === "Awaiting Settlement" ? (
                          <div className="text-[10px] font-bold text-[#6375E6]">
                            {status.toUpperCase()}
                          </div>
                        ) : status === "Complete" || status === "Completed" ? (
                          <div className="text-[10px] font-bold text-[#17C3A5]">
                            {status.toUpperCase()}
                          </div>
                        ) : (
                          <div className="text-[10px] font-bold text-[#FFC107]">
                            {status.toUpperCase()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="col-span-3 hidden md:block">
                    {status && (
                      <div className="flex items-center gap-2 text-[14px] font-bold">
                        {status === "Pending" ||
                        status === "Awaiting Settlement" ? (
                          <div className="flex items-center gap-2 bg-[#EFF1FC] px-3 py-1 rounded-full">
                            <StatusIcon color="#6375E6" />
                            <span className="text-[10px] font-bold text-[#6375E6]">
                              {status.toUpperCase()}
                            </span>
                          </div>
                        ) : status === "Complete" || status === "Completed" ? (
                          <div className="flex items-center gap-2 bg-[#E8F9F6] px-3 py-1 rounded-full">
                            <StatusIcon color="#17C3A5" />
                            <span className="text-[10px] font-bold text-[#17C3A5]">
                              {status.toUpperCase()}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 bg-[#FFEFD5] px-3 py-1 rounded-full">
                            <StatusIcon color="#FFC107" />
                            <span className="text-[10px] font-bold text-[#FFC107]">
                              {status.toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="col-span-1 justify-end hidden md:flex">
                    <Button
                      onClick={() => handleSingleTransaction(transaction)}
                      variant="outline"
                      size="icon"
                      className="rounded-full flex-shrink-0 border-gray-200 hover:bg-gray-200 h-10 w-10"
                    >
                      <ArrowRight className="w-4 h-4 text-[#6D6584]" />
                    </Button>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="flex items-center justify-center py-8 text-[#6D6584]">
              No transactions found.
            </p>
          )}
        </div>
        {singleTransaction && (
          <TransactionHistorySingle
            selectedTransaction={selectedTransaction}
            openSingle={singleTransaction}
            setOpenSingle={setSingleTransaction}
            onClose={handleReOpenTHModal}
          />
        )}
        {transactionsAll && (
          <TransactionHistoryAll
            transactionsComplete={filteredAndSortedTransactions}
            openAll={transactionsAll}
            setOpenAll={setTransactionsAll}
            onOpenSingle={handleSingleTransaction}
            setIsFromAllTr={setIsFromAllTr}
            walletAssetsData={
              asset
                ? {
                    assets: [
                      {
                        asset:
                          asset.symbol ||
                          asset.asset ||
                          asset.info?.asset ||
                          "",
                        image: asset.image || asset.info?.image || "",
                      },
                    ],
                  }
                : null
            }
            filterType={filterType}
            filterStatus={filterStatus}
            sortBy={sortBy}
            setFilterType={setFilterType}
            setFilterStatus={setFilterStatus}
            setSortBy={setSortBy}
            formatter={formatter}
            formatTransactionDate={formatTransactionDate}
            getTransactionIconComponent={getTransactionIconComponent}
            getTransactionActionText={getTransactionActionText}
            getTransactionActionTextMobile={getTransactionActionTextMobile}
            getTransactionAmounts={getTransactionAmounts}
          />
        )}
      </CardContent>
    </Card>
  );
}
